import { useMemo } from 'react';
import { BookMarked, ExternalLink, FileText } from 'lucide-react';
import type { KbCitation, KbSource } from '../../api';

/** Derives a short, readable label from a source URI (basename / host). */
function sourceLabel(source: KbSource, index: number): string {
  if (!source.uri) return `Source ${index + 1}`;
  try {
    if (source.uri.startsWith('s3://')) {
      const parts = source.uri.replace('s3://', '').split('/');
      return parts[parts.length - 1] || source.uri;
    }
    const url = new URL(source.uri);
    const base = url.pathname.split('/').filter(Boolean).pop();
    return base || url.hostname;
  } catch {
    const parts = source.uri.split('/');
    return parts[parts.length - 1] || source.uri;
  }
}

/** A single source row: label + optional link + snippet. */
function SourceRow({ source, index }: { source: KbSource; index: number }) {
  const label = sourceLabel(source, index);
  const snippet =
    source.text.length > 320 ? `${source.text.slice(0, 319)}…` : source.text;
  return (
    <li className="rounded-lg border border-surface-border bg-surface-2/60 px-3 py-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-300">
        <FileText className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
        {source.uri ? (
          <a
            href={source.uri}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 truncate text-accent hover:underline"
            title={source.uri}
          >
            <span className="truncate">{label}</span>
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
          </a>
        ) : (
          <span className="truncate">{label}</span>
        )}
        {source.locationType && (
          <span className="ml-auto shrink-0 rounded bg-surface-3 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500">
            {source.locationType}
          </span>
        )}
      </div>
      {snippet && (
        <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-zinc-500">
          {snippet}
        </p>
      )}
    </li>
  );
}

/**
 * Renders the KB citations for a grounded assistant turn as a Sources panel:
 * each unique retrieved source shows its URI (linked when available) and the
 * supporting snippet. De-dupes sources across citation spans by URI+text.
 */
export function CitationsPanel({ citations }: { citations: KbCitation[] }) {
  const sources = useMemo(() => {
    const seen = new Set<string>();
    const out: KbSource[] = [];
    for (const c of citations) {
      for (const s of c.sources) {
        const key = `${s.uri ?? ''}::${s.text}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(s);
      }
    }
    return out;
  }, [citations]);

  if (sources.length === 0) return null;

  return (
    <div className="rounded-xl border border-surface-border bg-surface-2/40 px-3.5 py-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        <BookMarked className="h-3.5 w-3.5" aria-hidden="true" />
        Sources ({sources.length})
      </p>
      <ul className="space-y-1.5">
        {sources.map((s, i) => (
          <SourceRow key={`${s.uri ?? 'src'}:${i}`} source={s} index={i} />
        ))}
      </ul>
    </div>
  );
}

export default CitationsPanel;
